import { useEffect, useState } from "react";
import { PlaylistIndexManipulator } from "../../../playlists/PlaylistIndexManipulator";
import { Alert } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faHome,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { PlaylistFromUrlImportParser } from "./PlaylistFromUrlImportParser";
import Header from "../../../Header";
import { PlaylistLocalStoragePersistence } from "../../../localStoragePersistence/v4/playlist/PlaylistLocalStoragePersistence";
import { LoadingMessage } from "../../../LoadingMessage";
import { PlaylistRoutePaths } from "../../PlaylistRoutePaths";
import { useTranslation } from "react-i18next";

interface Props {
  importIdentifier: string;
}

enum ImportState {
  Working = 0,
  ImportSuccessful,
  AlreadyExists,
  FailureImportError,
}

const playlistLocalStoragePersistence = new PlaylistLocalStoragePersistence();

function PlaylistUrlImportDialog({ importIdentifier }: Props) {
  const { t } = useTranslation();
  const [importState, setImportState] = useState<ImportState>(
    ImportState.Working,
  );

  useEffect(() => {
    //Only run the effect once
    if (importState !== ImportState.Working) {
      return;
    }

    if (playlistLocalStoragePersistence.doesDtoExist(importIdentifier)) {
      setImportState(ImportState.AlreadyExists);
      return;
    }

    const importedPlaylist =
      new PlaylistFromUrlImportParser().createPlaylistFromUrl(
        importIdentifier,
        window.location.href,
      );
    if (importedPlaylist) {
      PlaylistIndexManipulator.importPlaylist(importedPlaylist);
      setImportState(ImportState.ImportSuccessful);
      // console.debug("PlaylistUrlImportDialog: Imported playlist", importedPlaylist.getIdentifier());
    } else {
      setImportState(ImportState.FailureImportError);
    }
  }, [importIdentifier, importState]);

  return (
    <>
      <Header />
      <br />
      {renderState()}
    </>
  );

  function renderState() {
    switch (importState) {
      case ImportState.Working:
        return renderWorking();
      case ImportState.AlreadyExists:
        return renderAlreadyExists();
      case ImportState.ImportSuccessful:
        return renderImportSuccessful();
      case ImportState.FailureImportError:
        return renderFailureImportError();
    }
  }

  function renderWorking() {
    return <LoadingMessage message={t("playlist.import.working")} />;
  }

  function renderImportSuccessful() {
    return (
      <Alert color="success" fade={false}>
        <h2 className={"light-header"}>
          <FontAwesomeIcon icon={faInfoCircle} />{" "}
          {t("playlist.import.successTitle")}
        </h2>
        <span>{t("playlist.import.successMessage")}</span>
        <br />
        <br />
        <Link to={PlaylistRoutePaths.toCarousel(importIdentifier)}>
          {t("playlist.import.openPlaylist")}
        </Link>
      </Alert>
    );
  }

  function renderAlreadyExists() {
    return (
      <Alert color="success" fade={false}>
        <h2 className={"light-header"}>
          <FontAwesomeIcon icon={faInfoCircle} />{" "}
          {t("playlist.import.alreadyExistsTitle")}
        </h2>
        <span>{t("playlist.import.alreadyExistsMessage")}</span>
        <br />
        <br />
        <Link to={PlaylistRoutePaths.toCarousel(importIdentifier)}>
          {t("playlist.import.openPlaylist")}
        </Link>
      </Alert>
    );
  }

  function renderFailureImportError() {
    return (
      <Alert color="danger" fade={false}>
        <h2 className={"light-header"}>
          <FontAwesomeIcon icon={faExclamationTriangle} />{" "}
          {t("playlist.import.errorTitle")}
        </h2>
        <span>{t("playlist.import.errorMessage")}</span>
        <br />
        <br />
        <Link to="/">
          <FontAwesomeIcon icon={faHome} /> {t("playlist.import.overview")}
        </Link>
      </Alert>
    );
  }
}

export default PlaylistUrlImportDialog;
